<template>
    <div>
        <h2 class="u-margin-top-0 u-margin-bottom-30">
            Service - {{ service.description }}
        </h2>

        <div class="flex-grid a-form-inputs">
            <div class="flex-grid__item">
                <control for="service_description" label="Service Description">
                    <input autocomplete="off" v-model="service.description"
                           required
                           id="service_description"
                           name="service_description"
                           v-field-control:service_description.required="{type:'text'}"
                           type="text">
                    <control-error :show="$error('service_description')">
                        The service description is required
                    </control-error>
                </control>
                <control for="services_multiplicand" label="Services Multiplicand" before-after="pound">
                     <input autocomplete="off" v-model.number="service.multiplicand"
                           required
                           v-two-dp
                           min="1"
                           id="services_multiplicand"
                           @input="updateServicePeriod()"
                           v-field-control:services_multiplicand.required="{type:'number', rules:'min:0.01'}"
                            v-on:focus="service.multiplicand = service.multiplicand || ''"
                            v-on:blur="service.multiplicand = service.multiplicand || 0"
                           type="text">
                    <control-error :show="$error('servicesMultiplicand')">
                        The services multiplicand is required and has to be greater than 0
                    </control-error>
                </control>
                <control for="services_start_date" label="Services Start Date">
                    <date-input v-model="service.servicesStartDate"
                                @input="updateServicePeriod()"
                                v-field-control:services_start_date.required="{type:'date'}"
                    ></date-input>
                    <control-error :show="$error('services_start_date')">
                        The services start date is required and must be in the format DD/MM/YYYY
                    </control-error>
                    <control-error :show="!servicesStartDateValidation">
                        The services start date must be after the date of Injury/Accident {{ getClaimInfo('dateOfInjury') }}
                    </control-error>
                </control>
                <control for="mortality_multiplier" label="Adjust Multiplier for Mortality?">
                    <select id="mortality_multiplier"
                            v-model="service.mortalityMultiplier"
                            @change="updateServicePeriod()"
                            v-field-control
                            required>
                        <option :value="null" disabled hidden>Please select...</option>
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </control>
                <button type="button" class="a-button a-button--no-margin" @click="deleteService">Remove Services</button>
            </div>
            <div class="flex-grid__item flex-grid--space-left">
                <control for="services_losses_for_life" label="Do Services Continue for Life?">
                    <select id="services_losses_for_life"
                            v-model="service.lossesForLife"
                            v-field-control
                            required
                            @change="getServicesLifeExpectancy">
                        <option :value="null" disabled hidden>Please select...</option>
                        <option :value="true">Yes</option>
                        <option :value="false">No</option>
                    </select>
                </control>
                <life-expectancy
                        v-if="service.lossesForLife"
                        label="Claimant's 'But For' Life Expectancy"
                        :person="thePerson"
                        :date="theDate"
                        type="deceased"
                        :assumedSettlement="getClaimInfo('dateOfAssumedSettlement')"
                        :stepValid="stepValid"
                        @update="updateLife"
                ></life-expectancy>
                <control for="services_end_date" label="Services End Date">
                    <date-input v-model="service.servicesEndDate"
                                :disabled="service.lossesForLife"
                                @input="updateServiceEndDate()"
                                v-field-control:services_end_date.required="{type:'date'}"
                    ></date-input>
                    <control-error :show="$error('services_end_date')">
                        The services end date is required and must be in the format DD/MM/YYYY
                    </control-error>
                    <control-error :show="!servicesEndDateValidation">
                        The services end date must be after the services start date  {{ service.servicesStartDate }}
                    </control-error>
                </control>
                <control for="services_age_at_end_date" label="Age at End Date" v-if="service.lossesForLife === false">
                    <input autocomplete="off" v-model.number="service.ageAtEndDate"
                           required
                           v-two-dp
                           min="1"
                           id="services_age_at_end_date"
                           @input="updateServiceAge()"
                           v-field-control:services_age_at_end_date.required="{type:'number', rules:'min:0.01'}"
                           type="text">
                    <control-error :show="$error('services_age_at_end_date')">
                        The claimant's age at services end date is required and has to be greater than 0
                    </control-error>
                </control>
            </div>
        </div>
    </div>
</template>
<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import DateInput from 'components/form/control/DateInput.vue';
    import FormValidator from 'components/form/validator/mixin';
    import LifeExpectancy from 'components/LifeExpectancy.vue';
    import roundValue from 'mixins/roundValue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import {mapActions, mapGetters} from 'vuex';

    export default {
        name: 'future-services',
        props: {
            index: {
                type: Number,
                default: 0
            },
            service: {
                type: Object,
                required: true
            },
        },
        mixins: [
            FormValidator,
            currencyFormat,
            roundValue,
        ],
        components: {
            Control,
            ControlError,
            DateInput,
            LifeExpectancy,
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        data() {
            return {
                theDate: moment().format('DD/MM/YYYY')
            }
        },
        mounted() {
            if (this.service.servicesStartDate === null) {
                this.service.servicesStartDate = this.getClaimInfo('dateOfAssumedSettlement');
            }
        },
        computed: {
            ...mapGetters('personal/otherFutureLosses', [
                'value'
            ]),
            ...mapGetters('personal/claimant', [
                'claimant',
                'getClaimInfo',
            ]),
            thePerson() {
                return {
                    lifeExpectancy: this.value('servicesLifeExpectancy'),
                    gender: this.claimant.gender,
                    dateOfBirth: this.claimant.dateOfBirth
                };
            },
            servicesStartDateValidation: {
                get() {
                    let mStartOfService = moment(this.service.servicesStartDate, 'DD/MM/YYYY');
                    let mDateOfAccident = moment(this.getClaimInfo('dateOfInjury'), 'DD/MM/YYYY');

                    if (!mStartOfService.isValid() || !mDateOfAccident.isValid()) {
                        return false;
                    }

                    return mStartOfService.isSameOrAfter(mDateOfAccident);
                }
            },
            servicesEndDateValidation: {
                get() {
                    let mStartOfService = moment(this.service.servicesStartDate, 'DD/MM/YYYY');
                    let mEndOfService = moment(this.service.servicesEndDate, 'DD/MM/YYYY');

                    if (!mStartOfService.isValid() || !mEndOfService.isValid()) {
                        return false;
                    }

                    return mEndOfService.isSameOrAfter(mStartOfService);
                }
            },
        },
        methods: {
            ...mapActions('personal/otherFutureLosses', [
                'set',
                'setServices',
                'updateServices',
                'removeService',
                'updateLifeForServices'
            ]),
            updateLife(lifeExpectancy) {
                this.updateLifeForServices({lifeExpectancy, index: this.index})
            },
            getServicesLifeExpectancy() {
                if (this.service.lossesForLife === true) {
                    let lifeExpectancy = this.value('servicesLifeExpectancy');
                    this.updateLife(lifeExpectancy)
                }
                this.updateServicePeriod();
            },
            deleteService() {
                this.removeService({index: this.index})
            },
            updateServicePeriod() {
                this.updateServices({index: this.index})
            },
            updateServiceEndDate() {
                this.service.ageAtEndDate = moment(this.service.servicesEndDate, 'DD/MM/YYYY').diff(moment(this.claimant.dateOfBirth, 'DD/MM/YYYY') ,'years' ,true);
                this.updateServicePeriod();
            },
            updateServiceAge() {
                this.service.servicesEndDate = moment(this.claimant.dateOfBirth, 'DD/MM/YYYY').add(this.service.ageAtEndDate , 'y').format('DD/MM/YYYY');
                this.updateServicePeriod();
            }
        },

    }
</script>


// WEBPACK FOOTER //
// FutureService.vue?4d937da6